import log from "loglevel";
import { useCallback } from "react";

import type { Module } from "ee/constants/ModuleConstants";
import type { Package } from "ee/constants/PackageConstants";

type LastVisited = Record<Package["id"], Module["id"] | undefined>;
interface LogLastVisitedProps {
  baseModuleId: Module["id"];
}

interface UseLastVisitedProps {
  basePackageId: Package["id"];
  key: string;
}

export const getValues = (key: string) => {
  try {
    const values = localStorage.getItem(key) || "{}";

    return JSON.parse(values) as LastVisited;
  } catch (error) {
    log.error(error);

    return undefined;
  }
};

const setValues = (values: LastVisited, key: string) => {
  try {
    // Save to local storage
    localStorage.setItem(key, JSON.stringify(values));
  } catch (error) {
    // A more advanced implementation would handle the error case
    log.error(error);
  }
};

export function useLastVisitedModule({
  basePackageId,
  key,
}: UseLastVisitedProps) {
  const getLastVisited = useCallback(() => {
    const values = getValues(key) || {};

    return values[basePackageId];
  }, [getValues, basePackageId]);

  const logLastVisited = useCallback(
    ({ baseModuleId }: LogLastVisitedProps) => {
      const values = getValues(key) || {};

      values[basePackageId] = baseModuleId;
      setValues(values, key);
    },
    [basePackageId, getValues, setValues],
  );

  return {
    logLastVisited,
    getLastVisited,
  };
}
